import { useCallback, useEffect, useRef, useState } from 'react';
import { apiPost } from '../utils/api';

export function promotionPreviewChanged(previous, next) {
  const signature = (summary) => [
    Number(summary?.applied_promotion?.promotion_content_id || 0),
    Number(summary?.promotion_discount_amount || 0).toFixed(2),
    Number(summary?.total_amount || 0).toFixed(2),
  ].join('|');
  return signature(previous) !== signature(next);
}

// Only our local pricing API is polled. This never calls Traveltek or creates a basket.
export default function usePromotionPreview(payload, isBusy) {
  const key = payload ? JSON.stringify(payload) : '';
  const [state, setState] = useState({ key: '', summary: null });
  const sequence = useRef(0);
  const alive = useRef(false);
  const latestKey = useRef(key);
  const busy = useRef(isBusy);
  latestKey.current = key;
  busy.current = isBusy;

  const refresh = useCallback(async () => {
    if (!key) return null;
    const requestNo = ++sequence.current;
    const started = Date.now();
    let response;
    try {
      response = await apiPost('/reservation/promotion_preview.php', JSON.parse(key));
    } catch (error) {
      if (!alive.current || latestKey.current !== key || requestNo !== sequence.current) throw new Error('STALE_PROMOTION_PREVIEW');
      throw error;
    }
    if (!alive.current || latestKey.current !== key || requestNo !== sequence.current) {
      throw new Error('STALE_PROMOTION_PREVIEW');
    }
    if (response?.success !== true || !response.payment_summary) {
      throw new Error('PROMOTION_PREVIEW_FAILED');
    }
    setState({ key, summary: response.payment_summary });
    return {
      ...response,
      // Account for request latency without depending on the browser's wall clock.
      refresh_after_ms: Math.max(1000, Math.min(60000, Number(response.refresh_after_ms) || 60000) - (Date.now() - started)),
    };
  }, [key]);

  useEffect(() => {
    alive.current = true;
    let stopped = false;
    let running = false;
    let timer;
    const update = async () => {
      if (stopped || !key || running) return;
      clearTimeout(timer);
      if (busy.current?.() || document.hidden) {
        timer = setTimeout(update, 30000);
        return;
      }
      running = true;
      let delay = 30000;
      try {
        const response = await refresh();
        delay = response?.refresh_after_ms || 60000;
      } catch (error) {
        if (!stopped && error.message !== 'STALE_PROMOTION_PREVIEW') setState({ key, summary: null });
      } finally {
        running = false;
        if (!stopped) timer = setTimeout(update, delay);
      }
    };
    const onFocus = () => { if (!document.hidden) update(); };
    if (key) update();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      stopped = true;
      alive.current = false;
      sequence.current += 1;
      clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [key, refresh]);

  return { summary: state.key === key ? state.summary : null, refresh, enabled: Boolean(key) };
}
